import React from "react";
import Toaster from "../Toaster";
import EmailEditor from "../emaileditor/EmailEditor";
import ApiQc from "../../code/api/ApiQc";
import CustomerQcResult from "../../code/models/CustomerQcResult";
import MessageModalForm from "../modal-forms/MessageModalForm";
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from "../CclDataTable";
import ApiCustomer from "../../code/api/ApiCustomer";
import CustomerStats from "../../code/models/CustomerStats";
import BidPlanInfo from "../../code/models/BidPlanInfo";
import ApiBidding from "../../code/api/ApiBidding";
import AuthAdminUserModel from "../../code/models/AuthAdminUserModel";
import MessageLogDataTable from "../MessageLogDataTable";
import Api_Config from "../../code/api/ApiConfig";

import "../../css/adminqc.css";
import Popout from "../modal-forms/Popout";
import { TextTemplateInfo } from "../emaileditor/TextTemplatePlugin";
import { TextTemplateButtonInfo } from "../emaileditor/TextTemplateButtonPlugin";
import CustomerInfoProfiles from "../CustomerInfoProfiles";
import { stringToDate, stringToDateTime } from "../../code/helpers/stringToDate";

const logoSendGreen = require('../../images/send-green.png');
const logoSendBlue = require('../../images/send-blue.png');
const logoSendWhite = require('../../images/send-white.png');


interface IAdminQcForm {
    adminUser: AuthAdminUserModel;
}

export default function AdminQcForm({ adminUser }: IAdminQcForm) {
    const [toastMessage, setToastMessage] = React.useState<string>("");

    const [qcItem, setQcItem] = React.useState<CustomerQcResult | null>(null);
    const [qcMessage, setQcMessage] = React.useState<string>("");
    const [showInternalNotes, setShowInternalNotes] = React.useState<boolean>(false);
    const [showMessageLog, setShowMessageLog] = React.useState<boolean>(false);
    const [showProfileList, setShowProfileList] = React.useState<boolean>(false);
    const [qcItems, setQcItems] = React.useState<CustomerQcResult[]>([]);
    const [qcFilterItems, setQcFilterItems] = React.useState<CustomerQcResult[]>([]);

    const [customerStats, setCustomerStats] = React.useState<CustomerStats | null>(null);
    const [bidPlanInfo, setBidPlanInfo] = React.useState<BidPlanInfo | null>(null);

    const [filterName, setFilterName] = React.useState<string>("");
    const [tableKey, setTableKey] = React.useState<string>("aqc0121");

    const [qcTextTemplates, setQcTextTemplates] = React.useState<TextTemplateInfo[] | null>(null);
    const [qcButtonTemplates, setQcButtonTemplates] = React.useState<TextTemplateButtonInfo[] | null>(null);

    const [forceQcFilterTrigger, setForceQcFilterTrigger] = React.useState<number>(0);

    let company = Api_Config.getSelectedCompany();

    React.useEffect(() => {
        loadData();
        window.addEventListener('beforeunload', () => { setShowMessageLog(false); });
    }, []);

    async function loadData() {
        ApiQc.getQcItems().then((items) => {
            setQcItems(items || []);
            filterQcList(items || []);
        });

        ApiQc.getQuickNotes().then(notes => {
            if (!!notes) {
                var templateGroups = new Map<string, TextTemplateInfo[]>();

                notes.forEach(qn => {
                    let items: TextTemplateInfo[] = [];
                    if (templateGroups.has(qn.groupName)) {
                        items = templateGroups.get(qn.groupName) || [];
                    }

                    let t = new TextTemplateInfo();
                    t.text = qn.name;
                    t.template = qn.note;

                    items.push(t);

                    templateGroups.set(qn.groupName, items);
                });

                if (templateGroups.has("Button")) {
                    var btnGrpLst = (templateGroups.get("Button") || []).map(btnInf => {
                        let bi = new TextTemplateButtonInfo();
                        bi.text = btnInf.text;
                        bi.template = btnInf.template;
                        return bi;
                    });

                    setQcButtonTemplates(btnGrpLst);

                    templateGroups.delete("Button");
                }

                let resultTemplates: TextTemplateInfo[] = [];
                templateGroups.forEach((children, displayName) => {
                    let t = new TextTemplateInfo();
                    t.text = displayName;
                    t.template = "";
                    t.children = children;

                    resultTemplates.push(t);
                });
                setQcTextTemplates(resultTemplates);
            }
        });
    }

    React.useEffect(() => {
        setCustomerStats(null);
        setBidPlanInfo(null);
        if (qcItem !== null) {
            localStorage.setItem(`qc.currentItem:${company.companyId}`, JSON.stringify(qcItem));

            ApiCustomer.getCustomerStats(qcItem.customerId).then((stat) => {
                setCustomerStats(stat);
            });
            // get last bid plan
            ApiBidding.getBidPlanInfo(qcItem.customerId, 0).then((bpInfo) => {
                setBidPlanInfo(bpInfo);
            });
        }
    }, [qcItem]);

    React.useEffect(() => {
        filterQcList(qcItems).then((filteredItems) => {
            if (!!qcItem) {
                var idx = filteredItems.findIndex((qcr: CustomerQcResult) => {
                    return qcr.customerQcResultId === qcItem.customerQcResultId;
                });

                if (idx < 0) {
                    setQcItem(null);
                    setQcMessage("");
                }
            }
        });
    }, [filterName, forceQcFilterTrigger]);

    function myItemsFilter(value: CustomerQcResult, index: number, array: CustomerQcResult[]) {
        return value.qcAdminId == adminUser.id;
    }

    function completeItemsFilter(value: CustomerQcResult, index: number, array: CustomerQcResult[]) {
        return value.qcComplete;
    }

    function followUpItemsFilter(value: CustomerQcResult, index: number, array: CustomerQcResult[]) {
        return value.needsFollowUp;
    }

    function noProfileItemsFilter(value: CustomerQcResult, index: number, array: CustomerQcResult[]) {
        return value.noProfile;
    }

    function newItemsFilter(value: CustomerQcResult, index: number, array: CustomerQcResult[]) {
        return !value.qcAdminId;
    }

    async function filterQcList(itemsToFilter: CustomerQcResult[]): Promise<CustomerQcResult[]> {
        let filter: any;

        switch (filterName) {
            case "mine":
                filter = myItemsFilter;
                break;
            case "complete":
                filter = completeItemsFilter;
                break;
            case "follow-up":
                filter = followUpItemsFilter;
                break;
            case "no-profile":
                filter = noProfileItemsFilter;
                break;
            case "new":
                filter = newItemsFilter;
                break;
            case "all":
            default:
                setQcFilterItems([...itemsToFilter]);
                return [...itemsToFilter];
        }

        let filteredItems = itemsToFilter.filter(filter);
        setQcFilterItems(filteredItems);

        return filteredItems;
    }

    const qcColumnDefs: CclDataTableColumnDef[] = [
        {
            type: CclDataTableColumnType.Data,
            fieldName: "customerName",
            title: "Name",
            visible: true,
            className: "adminqc-qclist-custname",
            sortable: true
        },
        {
            type: CclDataTableColumnType.Data,
            fieldName: "qcAdminLoginId",
            title: "QC",
            visible: true,
            className: "adminqc-qclist-adminname"
        }
    ];

    function qcItemSelected(rowData: CustomerQcResult): void {
        setQcItem(rowData);
        setQcMessage(rowData.customerNotes || "");
    }

    function editInternalNotes(): void {
        setShowInternalNotes(true);
    }

    async function saveQcItem(qcItem: CustomerQcResult): Promise<void> {
        await ApiQc.updateQcItem(qcItem).then((qci) => {
            if (!!qci) {
                qcItem.qcAdminId = qci.qcAdminId;
                qcItem.qcAdminName = qci.qcAdminName;
                qcItem.qcAdminLoginId = qci.qcAdminLoginId;
            }

            makeListKey();
            sendToastMessage(`QC Item Saved for: ${qci.customerName}`);
        });
    }

    function sendToastMessage(message: string) {
        setToastMessage(message);
        setTimeout(() => { setToastMessage(""); }, 4000);
    }


    function internalNotesModalConfirm(isConfirm: boolean, message?: string | undefined): void {
        if (isConfirm && !!qcItem) {
            qcItem.internalNotes = message || "";
            saveQcItem(qcItem);
        }
        setShowInternalNotes(false);
    }

    function getQCStatus(qcItem: CustomerQcResult | null): React.ReactNode {
        if (qcItem?.qcComplete) {
            return "QC Complete";
        } else if (qcItem?.needsFollowUp) {
            return "Needs Follow Up";
        } else if (qcItem?.noProfile) {
            return "No Profile Built";
        } else if (!!qcItem?.qcAdminName) {
            return "QC By " + qcItem?.qcAdminName;
        }
        return "";
    }

    function getFilterButtonClass(buttonName: string): string | undefined {
        var btnMarkup = buttonName === filterName ? "btn-secondary" : "btn-outline-secondary";
        return `btn btn-sm ${btnMarkup} me-2 adminqc-filterbutton`;
    }

    function startQcClick(): void {
        if (!qcItem) return;
        qcItem.qcAdminId = adminUser.id;
        qcItem.qcAdminName = adminUser.name;
        qcItem.qcAdminDate = new Date();
        saveQcItem(qcItem);
    }

    function cancelQcClick(): void {
        if (!qcItem) return;
        qcItem.qcAdminId = null;
        qcItem.qcAdminDate = null;
        qcItem.needsFollowUp = false;
        qcItem.needsFollowUpDate = null;
        qcItem.noProfile = false;
        qcItem.needsFollowUpDate = null;
        qcItem.qcComplete = false;
        qcItem.qcCompleteDate = null;
        saveQcItem(qcItem);
    }

    function completeQcClick(): void {
        if (!qcItem) return;
        qcItem.qcAdminId = adminUser.id;
        qcItem.qcAdminName = adminUser.name;
        qcItem.qcAdminDate = new Date();
        qcItem.qcComplete = true;
        qcItem.qcCompleteDate = new Date();
        saveQcItem(qcItem).then(() => {
            doQcComplete();
        });
    }

    function doQcComplete() {
        setForceQcFilterTrigger(new Date().getTime());
    }


    function openBidNavClick(): void {
        if (!qcItem) return;
        let url = Api_Config.getBidNavUrlForCustomer(qcItem.customerId, adminUser.id);
        window.open(url, "BidNav_QC");
    }

    function toggleMessageLog(): void {
        if (!qcItem) return;
        setShowMessageLog(!showMessageLog);
    }

    function toggleProfileList(): void {
        if (!qcItem) return;
        setShowProfileList(!showProfileList);
    }

    function followUpClick(): void {
        if (!qcItem) return;
        qcItem.qcAdminId = adminUser.id;
        qcItem.qcAdminDate = new Date();
        qcItem.needsFollowUp = true;
        qcItem.needsFollowUpDate = new Date();
        qcItem.noProfile = false;
        qcItem.needsFollowUpDate = null;
        qcItem.qcComplete = false;
        qcItem.qcCompleteDate = null;
        saveQcItem(qcItem);
    }

    function noProfileClick(): void {
        if (!qcItem) return;
        ApiQc.sendNoProfileBuilt(qcItem.customerQcResultId).then((qci) => {
            if (!!qci) {
                qcItem.qcAdminId = qci.qcAdminId;
                qcItem.qcAdminDate = qci.qcAdminDate;
                qcItem.qcAdminName = qci.qcAdminName;
                qcItem.qcAdminLoginId = qci.qcAdminLoginId;
                qcItem.needsFollowUp = qci.needsFollowUp;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.noProfile = qci.noProfile;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.qcComplete = qci.qcComplete;
                qcItem.qcCompleteDate = qci.qcCompleteDate;
                qcItem.customerNotes = qci.customerNotes;

                sendToastMessage(`No Profile sent to  ${qci.customerName}`);
            }
        });
    }

    function looksGoodClick(): void {
        if (!qcItem) return;
        ApiQc.sendProfileLooksGood(qcItem.customerQcResultId).then((qci) => {
            if (!!qci) {
                qcItem.qcAdminId = qci.qcAdminId;
                qcItem.qcAdminDate = qci.qcAdminDate;
                qcItem.qcAdminName = qci.qcAdminName;
                qcItem.qcAdminLoginId = qci.qcAdminLoginId;
                qcItem.needsFollowUp = qci.needsFollowUp;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.noProfile = qci.noProfile;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.qcComplete = qci.qcComplete;
                qcItem.qcCompleteDate = qci.qcCompleteDate;
                qcItem.customerNotes = qci.customerNotes;

                setQcMessage(qci.customerNotes);

                sendToastMessage(`Profile Looks Good sent to  ${qci.customerName}`);
            }
        });
    }

    function sendEmailClick(): void {
        if (!qcItem) return;
        qcItem.customerNotes = qcMessage;

        ApiQc.sendQcMessage(false, qcItem).then((qci) => {
            if (!!qci) {
                qcItem.qcAdminId = qci.qcAdminId;
                qcItem.qcAdminDate = qci.qcAdminDate;
                qcItem.qcAdminName = qci.qcAdminName;
                qcItem.qcAdminLoginId = qci.qcAdminLoginId;
                qcItem.needsFollowUp = qci.needsFollowUp;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.noProfile = qci.noProfile;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.qcComplete = qci.qcComplete;
                qcItem.qcCompleteDate = qci.qcCompleteDate;
                qcItem.customerNotes = qci.customerNotes;

                setQcMessage(qci.customerNotes);

                sendToastMessage(`Message sent to  ${qci.customerName}`);
            }
        });
    }

    function sendEmailCompleteClick(): void {
        if (!qcItem) return;
        qcItem.customerNotes = qcMessage;

        ApiQc.sendQcMessage(true, qcItem).then((qci) => {
            if (!!qci) {
                qcItem.qcAdminId = qci.qcAdminId;
                qcItem.qcAdminDate = qci.qcAdminDate;
                qcItem.qcAdminName = qci.qcAdminName;
                qcItem.qcAdminLoginId = qci.qcAdminLoginId;
                qcItem.needsFollowUp = qci.needsFollowUp;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.noProfile = qci.noProfile;
                qcItem.needsFollowUpDate = qci.needsFollowUpDate;
                qcItem.qcComplete = qci.qcComplete;
                qcItem.qcCompleteDate = qci.qcCompleteDate;
                qcItem.customerNotes = qci.customerNotes;
                setQcMessage(qci.customerNotes);
                doQcComplete();

                sendToastMessage(`Message sent to  ${qci.customerName}.  QC Completed.`);
            }
        });
    }

    function saveMessageClick(): void {
        if (!qcItem) return;
        qcItem.customerNotes = qcMessage;
        saveQcItem(qcItem);
    }

    function refreshButtonClick(): void {
        if (!qcItem) {
            setQcItem(null);
            setQcMessage("");
        }

        loadData().then(() => {
            filterQcList(qcItems);
        });
    }

    function makeListKey() {
        if (!qcItem) {
            setTableKey(`aqc000000`);
        } else {
            setTableKey(`aqc${qcItem.customerQcResultId}${qcItem.qcAdminId}${qcItem.qcComplete}${qcItem.noProfile}${qcItem.needsFollowUp}`);
        }
    }

    function renderMessagePopout() {
        if (!qcItem || !showMessageLog) return;
        return (
            <Popout title='QC - Message Log' closeWindow={() => setShowMessageLog(false)}>
                <div className="container-fluid d-flex h-100 w-100">
                    <MessageLogDataTable currentUser={adminUser} customerId={qcItem.customerId} key={"aqcml" + qcItem.customerId} />
                </div>
            </Popout>
        )
    }

    function renderProfileListPopout() {
        if (!qcItem || !showProfileList) return;
        return (
            <Popout title='QC - Profile List' closeWindow={() => setShowProfileList(false)}>
                <div className="container-fluid d-flex h-100 w-100">
                    <CustomerInfoProfiles customerId={qcItem.customerId} key={"aqcml" + qcItem.customerId} />
                </div>
            </Popout>
        )
    }

    function formatDate(val: any): string {
        if (!val) return "None";
        return `${stringToDate(val + "")}`;
    }

    return (
        <React.Fragment>
            <div className="container-fluid h-100 d-flex">
                <div className="h-100 d-flex flex-column adminqc-qclist">
                    <div className="flex-column adminqc-qclist-buttons">
                        <div className="ms-1 mt-0">
                            <button className={getFilterButtonClass("all")}
                                onClick={() => setFilterName("all")}>All</button>
                            <button className={getFilterButtonClass("mine")}
                                onClick={() => setFilterName("mine")}>Mine</button>
                            <button className={getFilterButtonClass("complete")}
                                onClick={() => setFilterName("complete")}>Complete</button>
                        </div>
                        <div className="ms-1 mt-2">
                            <button className={getFilterButtonClass("follow-up")}
                                onClick={() => setFilterName("follow-up")}>Follow Up</button>
                            <button className={getFilterButtonClass("no-profile")}
                                onClick={() => setFilterName("no-profile")}>No Profile</button>
                            <button className={getFilterButtonClass("new")}
                                onClick={() => setFilterName("new")}>New</button>
                        </div>
                    </div>
                    <div className="row adminqc-qclist-list" key={tableKey}>
                        <div className="row adminqc-qclist-listwrapper">
                            <CclDataTable name={"qcItemsTable"}
                                tableData={qcFilterItems || []}
                                rowKeyFields={["customerQcResultId", "qcAdminId", "qcComplete", "needsFollowUp", "noProfile"]}
                                columnDefinitions={qcColumnDefs}
                                rowSelect={true}
                                defaultSelectedRow={qcItem}
                                onRowSelect={qcItemSelected}
                                onButtonClick={() => { }}
                                sortable={true} />
                        </div>
                    </div>
                    <div className="adminqc-qclist-bottom">
                        <span className="float-start">Count: {qcFilterItems?.length}</span>
                        <button className="btn btn-sm btn-primary" onClick={refreshButtonClick}>Refresh</button>
                    </div>
                </div>
                <div className="container-flow d-flex flex-column adminqc-main">
                    <div className="ms-3 adminqc-customerinfo" key={"aqcci" + qcItem?.customerQcResultId}>
                        <div className="row">
                            <div className="col-3 adminqc-pilotinfo">
                                <div className="text-nowrap">
                                    <label className="me-2"><strong>Pilot Name:</strong></label>
                                    <span className="mt-2">{qcItem?.customerName || "Select a Customer"}</span>
                                </div>
                                <div className="text-nowrap">
                                    <label className="me-2"><strong>QC Status:</strong></label>
                                    <span>{getQCStatus(qcItem)}</span>
                                </div>
                            </div>
                            <div className="col-3 text-nowrap adminqc-fleetseatbase">
                                <div className="">
                                    <label className="me-2"><strong>Base:</strong></label>
                                    <span className="me-3">{customerStats?.base || "None"}</span>
                                    <label className="me-2"><strong>Aircraft:</strong></label>
                                    <span className="me-3">{customerStats?.aircraftGroup}</span>
                                    <label className="me-2"><strong>Position:</strong></label>
                                    <span>{customerStats?.position}</span>
                                </div>
                                <div className="">
                                    <label className="me-2"><strong>Last Plan Profile:</strong></label>
                                    <span className="me-3">{bidPlanInfo?.planName || "None"}</span>
                                </div>
                            </div>
                            <div className="col-2 text-nowrap">
                                <div className="">
                                    <label className="me-2"><strong>Created:</strong></label>
                                    <span className="me-3">{formatDate(qcItem?.createDate)}</span>
                                </div>
                                <div className="">
                                    <label className="me-2"><strong>Last QC:</strong></label>
                                    <span className="me-3">{formatDate(qcItem?.qcAdminDate)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="d-flex">
                                <button className="btn btn-sm btn-outline-primary" onClick={editInternalNotes} disabled={!qcItem}><strong>Internal Notes:</strong></button>
                                <span className="adminqc-internalnotes">{qcItem?.internalNotes || "None"}</span>
                            </div>
                        </div>
                    </div>
                    <div className="ms-4 adminqc-qcinfo">
                        <div className="row">
                            <div className="col p-0">
                                {!!qcItem && !!qcItem.qcAdminId &&
                                    <button className="btn btn-sm btn-outline-danger m-1 cancel-button" disabled={!qcItem} onClick={cancelQcClick}>Cancel QC</button>}
                                {(!qcItem || !qcItem.qcAdminId) &&
                                    <button className="btn btn-sm btn-outline-primary m-1" disabled={!qcItem} onClick={startQcClick}>Start QC</button>}
                                <button className="btn btn-sm btn-outline-primary m-1" disabled={!qcItem || !qcItem.qcAdminId} onClick={completeQcClick}>Complete QC</button>
                                <button className="btn btn-sm btn-outline-primary m-1" disabled={!qcItem || qcItem.needsFollowUp} onClick={followUpClick}>Follow Up Later</button>
                                <button className="btn btn-sm btn-outline-primary m-1" disabled={!qcItem} onClick={openBidNavClick}>Open BidNav</button>
                                <button className="btn btn-sm btn-outline-secondary m-1" disabled={!qcItem} onClick={toggleMessageLog}>{showMessageLog ? "Close Message Log" : "Message Log"}</button>
                                <button className="btn btn-sm btn-outline-secondary m-1" disabled={!qcItem} onClick={toggleProfileList}>{showProfileList ? "Close Profile List" : "Profile List"}</button>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col p-0">
                                <button className="btn btn-sm btn-outline-primary m-1 email-button blue" disabled={!qcItem || qcItem.noProfile} onClick={noProfileClick}>No Profile Built</button>
                                <button className="btn btn-sm btn-outline-success m-1 email-button green" disabled={!qcItem} onClick={looksGoodClick}>Profile Looks Good</button>
                                <button className="btn btn-sm btn-outline-primary m-1 email-button blue" disabled={!qcItem || qcMessage.length == 0} onClick={sendEmailClick}>Send Email</button>
                                <button className="btn btn-sm btn-outline-success m-1 email-button green" disabled={!qcItem || qcMessage.length == 0} onClick={sendEmailCompleteClick}>Send Email and Complete</button>
                                <button className="btn btn-sm btn-outline-secondary m-1 save-button" disabled={!qcItem} onClick={saveMessageClick}>Save Progress</button>
                            </div>
                        </div>
                    </div>
                    {!!qcItem && !!qcTextTemplates && !!qcButtonTemplates && <div className="row ms-3 adminqc-qceditor">
                        <EmailEditor onChange={setQcMessage} initialValue={qcItem?.customerNotes ?? ""} previewMode={false}
                            textTemplates={qcTextTemplates} templateButtons={qcButtonTemplates} />
                    </div>}
                </div>
            </div>
            {renderMessagePopout()}
            {renderProfileListPopout()}
            <MessageModalForm modalActive={showInternalNotes} modalTitle={"Internal Notes"} modalDescription={"Note"}
                onConfirm={internalNotesModalConfirm} message={qcItem?.internalNotes} />
            <Toaster title="Admin QC" subtitle={qcItem?.customerName || ""} message={toastMessage} key={"adminqctoast"} />
        </React.Fragment>
    )
}
